'use client';

import { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface Agent {
  id: string;
  name: string;
  icon: string;
  color: string;
  status: 'active' | 'scanning' | 'alert' | 'idle';
  alertCount: number;
  lastAction: string;
  confidence: number;
  influence: number;
  description: string;
  recentAlerts: { msg: string; severity: 'high' | 'medium' | 'low'; time: string }[];
}

const AGENTS: Agent[] = [
  {
    id: 'injury', name: 'InjuryAgent', icon: 'ExclamationTriangleIcon', color: '#ef4444',
    status: 'alert', alertCount: 3, lastAction: 'Injury report: BOS — D. Pastrnak questionable',
    confidence: 91, influence: 18,
    description: 'Surveillance blessures en temps réel, impact sur projections',
    recentAlerts: [
      { msg: 'Pastrnak (BOS) — questionable vs TOR', severity: 'high', time: '22:41' },
      { msg: 'Hedman (TBL) — day-to-day', severity: 'medium', time: '21:30' },
      { msg: 'Nurse (EDM) — practice limited', severity: 'low', time: '20:15' },
    ],
  },
  {
    id: 'goalie', name: 'GoalieAgent', icon: 'ShieldCheckIcon', color: '#3b82f6',
    status: 'active', alertCount: 1, lastAction: 'Goalie confirmed: Swayman starts vs TOR',
    confidence: 94, influence: 22,
    description: 'Confirmations gardiens, performance contextuelle, pression',
    recentAlerts: [
      { msg: 'Swayman confirmed — BOS vs TOR', severity: 'high', time: '22:38' },
    ],
  },
  {
    id: 'market', name: 'MarketAgent', icon: 'ChartBarIcon', color: '#f59e0b',
    status: 'alert', alertCount: 2, lastAction: 'Sharp money detected: BOS -1.5 pucks',
    confidence: 88, influence: 25,
    description: 'Mouvements marché, sharp money, anomalies lignes',
    recentAlerts: [
      { msg: 'Sharp movement: BOS -1.5 (line -2.5)', severity: 'high', time: '22:35' },
      { msg: 'Reverse line movement: TOR ML', severity: 'medium', time: '22:10' },
    ],
  },
  {
    id: 'momentum', name: 'MomentumAgent', icon: 'ArrowTrendingUpIcon', color: '#10b981',
    status: 'scanning', alertCount: 0, lastAction: 'Momentum scan: COL on 4-game win streak',
    confidence: 82, influence: 15,
    description: 'Momentum équipes, séquences, persistance tendances',
    recentAlerts: [],
  },
  {
    id: 'fatigue', name: 'FatigueAgent', icon: 'BoltIcon', color: '#a855f7',
    status: 'active', alertCount: 1, lastAction: 'B2B alert: TBL plays 2nd game in 24h',
    confidence: 86, influence: 12,
    description: 'Fatigue voyage, B2B, charge physique cumulative',
    recentAlerts: [
      { msg: 'TBL — B2B game, fatigue index: 81/100', severity: 'medium', time: '22:20' },
    ],
  },
  {
    id: 'simulation', name: 'SimulationAgent', icon: 'BeakerIcon', color: '#06b6d4',
    status: 'active', alertCount: 0, lastAction: 'Monte Carlo: BOS 67.3% win probability',
    confidence: 89, influence: 8,
    description: 'Simulations Monte Carlo, scénarios, probabilités',
    recentAlerts: [],
  },
];

const COORDINATION_LOG = [
  { time: '22:41', agents: ['InjuryAgent', 'GoalieAgent'], event: 'Pastrnak status → GoalieAgent recalibrates BOS xG projection', impact: -2.1 },
  { time: '22:38', agents: ['GoalieAgent', 'SimulationAgent'], event: 'Swayman confirmed → SimulationAgent updates win probability +4.2%', impact: +4.2 },
  { time: '22:35', agents: ['MarketAgent', 'MomentumAgent'], event: 'Sharp money + momentum convergence → HIGH confidence signal', impact: +6.8 },
  { time: '22:20', agents: ['FatigueAgent', 'SimulationAgent'], event: 'TBL B2B fatigue → SimulationAgent adjusts TBL projection -3.1%', impact: -3.1 },
];

const AGENT_ACTIVITY = Array.from({ length: 20 }, (_, i) => ({
  t: i,
  injury: Math.floor(Math.random() * 5),
  market: Math.floor(Math.random() * 8 + 2),
  goalie: Math.floor(Math.random() * 4 + 1),
  fatigue: Math.floor(Math.random() * 3),
}));

const STATUS_STYLES: Record<string, string> = {
  active: 'text-green-400 bg-green-400/10 border-green-400/30',
  scanning: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
  alert: 'text-red-400 bg-red-400/10 border-red-400/30',
  idle: 'text-muted-foreground bg-border/30 border-border',
};

export default function AutonomousAgentsClient() {
  const [selectedAgent, setSelectedAgent] = useState<Agent>(AGENTS[0]);
  const [totalAlerts, setTotalAlerts] = useState(AGENTS.reduce((s, a) => s + a.alertCount, 0));
  const [scanCount, setScanCount] = useState(12847);

  useEffect(() => {
    const interval = setInterval(() => {
      setScanCount(prev => prev + Math.floor(Math.random() * 10 + 3));
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="CpuChipIcon" size={20} className="text-orange-400" />
            <h1 className="text-xl font-bold text-foreground">Autonomous AI Agents</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-orange-400/10 text-orange-400 border border-orange-400/20 rounded">PHASE QUANT · ÉTAPE 8</span>
            <span className="flex items-center gap-1 px-2 py-0.5 text-2xs font-mono bg-green-400/10 text-green-400 border border-green-400/20 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />6 AGENTS ACTIFS
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Agents spécialisés autonomes avec coordination multi-agents</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">SCANS TOTAUX</div>
            <div className="text-lg font-bold text-orange-400 font-mono">{scanCount.toLocaleString()}</div>
          </div>
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">ALERTES ACTIVES</div>
            <div className="text-lg font-bold text-red-400 font-mono">{totalAlerts}</div>
          </div>
        </div>
      </div>

      {/* Agent Grid */}
      <div className="grid grid-cols-3 gap-3">
        {AGENTS.map(agent => (
          <div key={agent.id} onClick={() => setSelectedAgent(agent)}
            className={`bg-card border rounded-lg p-3 cursor-pointer transition-all hover:border-orange-400/50 ${selectedAgent.id === agent.id ? 'border-orange-400/60 bg-orange-400/5' : 'border-border'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon name={agent.icon as Parameters<typeof Icon>[0]['name']} size={16} style={{ color: agent.color }} />
                <span className="text-sm font-semibold text-foreground">{agent.name}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {agent.alertCount > 0 && (
                  <span className="px-1.5 py-0.5 text-2xs font-mono bg-red-400/10 text-red-400 border border-red-400/30 rounded">{agent.alertCount}</span>
                )}
                <span className={`text-2xs px-1.5 py-0.5 rounded border font-mono ${STATUS_STYLES[agent.status]}`}>
                  {agent.status.toUpperCase()}
                </span>
              </div>
            </div>
            <p className="text-2xs text-muted-foreground mb-2">{agent.description}</p>
            <div className="flex justify-between text-2xs">
              <span className="text-muted-foreground">Conf: <span className="text-foreground font-mono">{agent.confidence}%</span></span>
              <span className="text-muted-foreground">Influence: <span className="font-mono" style={{ color: agent.color }}>{agent.influence}%</span></span>
            </div>
            <div className="mt-2 h-1 bg-border rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${agent.confidence}%`, backgroundColor: agent.color }} />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Selected Agent Detail */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name={selectedAgent.icon as Parameters<typeof Icon>[0]['name']} size={18} style={{ color: selectedAgent.color }} />
            <h3 className="text-sm font-semibold text-foreground">{selectedAgent.name}</h3>
          </div>
          <div className="p-2 bg-background rounded border border-border mb-3">
            <div className="text-2xs text-muted-foreground mb-1">Dernière Action</div>
            <p className="text-xs text-foreground">{selectedAgent.lastAction}</p>
          </div>
          <div className="space-y-2">
            <div className="text-2xs text-muted-foreground font-mono uppercase mb-2">Alertes Récentes</div>
            {selectedAgent.recentAlerts.length === 0 ? (
              <div className="text-xs text-muted-foreground text-center py-4">Aucune alerte active</div>
            ) : (
              selectedAgent.recentAlerts.map((alert, i) => (
                <div key={i} className={`p-2 rounded border text-xs ${alert.severity === 'high' ? 'border-red-400/30 bg-red-400/5' : alert.severity === 'medium' ? 'border-yellow-400/30 bg-yellow-400/5' : 'border-border bg-background'}`}>
                  <div className="flex justify-between mb-0.5">
                    <span className={`text-2xs font-mono ${alert.severity === 'high' ? 'text-red-400' : alert.severity === 'medium' ? 'text-yellow-400' : 'text-muted-foreground'}`}>{alert.severity.toUpperCase()}</span>
                    <span className="text-2xs text-muted-foreground font-mono">{alert.time}</span>
                  </div>
                  <p className="text-foreground">{alert.msg}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Agent Activity Chart */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Activité Agents (Temps Réel)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={AGENT_ACTIVITY}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="t" tick={{ fill: '#64748b', fontSize: 9 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 9 }} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
              <Area type="monotone" dataKey="market" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={1.5} name="Market" />
              <Area type="monotone" dataKey="injury" stroke="#ef4444" fill="#ef4444" fillOpacity={0.15} strokeWidth={1.5} name="Injury" />
              <Area type="monotone" dataKey="goalie" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={1.5} name="Goalie" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Multi-Agent Coordination Log */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Coordination Multi-Agents</h3>
          <div className="space-y-2.5">
            {COORDINATION_LOG.map((log, i) => (
              <div key={i} className="p-2.5 bg-background rounded border border-border">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex gap-1">
                    {log.agents.map(a => (
                      <span key={a} className="text-2xs px-1.5 py-0.5 bg-orange-400/10 text-orange-400 border border-orange-400/20 rounded font-mono">{a.replace('Agent', '')}</span>
                    ))}
                  </div>
                  <span className="text-2xs text-muted-foreground font-mono">{log.time}</span>
                </div>
                <p className="text-xs text-foreground">{log.event}</p>
                <div className={`text-2xs font-mono mt-1 ${log.impact > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  Impact: {log.impact > 0 ? '+' : ''}{log.impact}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
